/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { Upload, Play, Target, TrendingUp, Award, Clock, BarChart3, CheckCircle, Download, CreditCard, Lock, AlertCircle, Shield } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './App.css';

// Initialize Stripe - Replace with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Debug Configuration
const DEBUG_MODE = false; // Set to false in production
const MOCK_MODE = false; // Set to true to test with sample video
const SAMPLE_VIDEO_URL = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';

// Secure Payment Form Component with Coupon Code Support
// Secure Payment Form Component with Coupon Code Support
const SecurePaymentForm = ({ jobId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Validate coupon when user types (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (couponCode.length > 0) {
        validateCoupon();
      } else {
        setDiscount(null);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [couponCode]);

  const validateCoupon = async () => {
    setValidatingCoupon(true);
    try {
      // Call backend to validate coupon
      const response = await fetch(`${API_URL}/create-payment-intent`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({ 
          jobId: jobId,
          user_ip: window.location.hostname,
          couponCode: couponCode,
          validateOnly: true // Add this flag to just validate without creating intent
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.discountedAmount !== undefined) {
          setDiscount(data.discountedAmount);
        }
      }
    } catch (err) {
      // Silently fail validation
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubmit = async (event) => {
    // Prevent default form submission if called from a form
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setPaymentError(null);
    setPaymentStatus('Creating secure payment...');

    try {
      // Create payment intent on backend
      const response = await fetch(`${API_URL}/create-payment-intent`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({ 
          jobId: jobId,
          user_ip: window.location.hostname,
          couponCode: couponCode
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment intent');
      }

      const data = await response.json();
      
      // Handle free access (100% discount)
      if (data.freeAccess) {
        setPaymentStatus('Free access granted! Redirecting...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        onSuccess({
          paymentIntentId: 'free_coupon',
          paymentStatus: 'succeeded'
        });
        return;
      }

      const { clientSecret, paymentIntentId, discountedAmount } = data;
      
      if (discountedAmount !== undefined) {
        setDiscount(discountedAmount);
      }
      
      setPaymentStatus('Processing payment...');

      // Confirm card payment
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            // You can add billing details here if needed
          }
        }
      });

      if (result.error) {
        setPaymentError(result.error.message);
        setPaymentStatus('');
      } else {
        setPaymentStatus('Payment successful! Verifying...');
        
        // Wait a moment for webhook to process
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Call parent success handler with payment info
        onSuccess({
          paymentIntentId: paymentIntentId,
          paymentStatus: 'succeeded'
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentError(err.message || 'Payment failed. Please try again.');
      setPaymentStatus('');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {paymentError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
          <span>{paymentError}</span>
        </div>
      )}
      
      {paymentStatus && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
          {paymentStatus}
        </div>
      )}
      
      {/* Coupon Code Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Discount Code (Optional)
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder="Enter code (e.g., FARZA)"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            disabled={processing}
          />
          {validatingCoupon && (
            <div className="absolute right-3 top-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500"></div>
            </div>
          )}
        </div>
        {discount !== null && discount < 999 && !validatingCoupon && (
          <p className="mt-2 text-sm text-green-600 font-medium">
            ✓ Discount applied! New price: ${(discount / 100).toFixed(2)}
          </p>
        )}
        {discount === 0 && !validatingCoupon && (
          <p className="mt-2 text-sm text-green-600 font-medium">
            ✓ 100% discount applied! This analysis is FREE!
          </p>
        )}
      </div>
      
      {/* Only show card input if not free */}
      {(discount === null || discount > 0) && (
        <div className="p-4 border border-gray-200 rounded-lg">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      )}
      
      <div className="flex items-center justify-between text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center">
          <Shield className="w-4 h-4 mr-2 text-green-600" />
          <span>Secure payment via Stripe</span>
        </div>
        <Lock className="w-4 h-4 text-gray-400" />
      </div>
      
      <button
        onClick={handleSubmit}
        disabled={!stripe || processing}
        className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
          processing
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-green-500 hover:bg-green-600 text-white'
        }`}
      >
        {processing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            {discount !== null && discount < 999 
              ? `Pay ${(discount / 100).toFixed(2)} & Download`
              : discount === 0 
              ? 'Get Free Video & Download'
              : 'Pay $9.99 & Download'}
          </>
        )}
      </button>
      
      <p className="text-xs text-gray-500 text-center">
        Your payment info is secure and never stored on our servers
      </p>
    </div>
  );
};

// Main Basketball Coach App Component
const BasketballCoachApp = () => {
  const [currentStep, setCurrentStep] = useState('upload'); // upload, processing, results
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [videoLoadError, setVideoLoadError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const fileInputRef = useRef(null);

  // Debug logging
  useEffect(() => {
    if (DEBUG_MODE) {
      console.log('Current state:', {
        currentStep,
        jobId,
        videoUrl,
        previewUrl,
        analysisResults,
        error: error || videoLoadError,
        paymentCompleted,
        accessToken
      });
    }
  }, [currentStep, jobId, videoUrl, previewUrl, analysisResults, error, videoLoadError, paymentCompleted, accessToken]);

  // Polling for job status
  useEffect(() => {
    if (jobId && currentStep === 'processing' && !MOCK_MODE) {
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`${API_URL}/status/${jobId}`, {
            headers: { "ngrok-skip-browser-warning": "true" }
          });

          if (!response.ok) {
            throw new Error(`Status check failed: ${response.status}`);
          }
          
          const data = await response.json();
          
          if (DEBUG_MODE) {
            console.log('Job status response:', data);
          }
          
          setJobStatus(data);
          
          if (data.status === 'completed') {
            // Set analysis results
            if (data.analysis_summary) {
              setAnalysisResults(data.analysis_summary);
            } else {
              console.warn('No analysis summary in response');
            }
            
            // Set preview URL (always available)
            setPreviewUrl(data.preview_url);
            
            // Check payment status
            if (data.payment_status === 'paid') {
              // If already paid, set video URL and access token
              setVideoUrl(data.video_url);
              setAccessToken(data.access_token);
              setPaymentCompleted(true);
            } else {
              // Video URL will not be available until payment
              console.log('Video ready but payment required');
            }
            
            setCurrentStep('results');
            clearInterval(interval);
          } else if (data.status === 'failed') {
            setError(data.error || 'Processing failed');
            setCurrentStep('upload');
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Error checking status:', err);
          setError(`Failed to check job status: ${err.message}`);
        }
      }, 2000); // Poll every 2 seconds
      
      return () => clearInterval(interval);
    }
  }, [jobId, currentStep]);

  // Handle successful payment
  const handlePaymentSuccess = async (paymentInfo) => {
    try {
      // Get job status again to retrieve access token and video URL
      const response = await fetch(`${API_URL}/status/${jobId}`, {
        headers: { "ngrok-skip-browser-warning": "true" }
      });

      if (!response.ok) {
        throw new Error('Failed to verify payment');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentCompleted(true);
        setAccessToken(data.access_token);
        setVideoUrl(data.video_url);
        
        if (DEBUG_MODE) {
          console.log('Payment verified, access token:', data.access_token);
        }
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError('Payment successful but verification failed. Please contact support.');
    }
  };

  // Secure video download with token verification
  const verifyAndDownload = async () => {
    if (!accessToken || !jobId) {
      setError('Missing access token. Please refresh the page.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/verify-payment/${jobId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({ 
          paymentToken: accessToken 
        })
      });

      if (!response.ok) {
        throw new Error('Download verification failed');
      }

      const data = await response.json();
      
      if (data.videoUrl) {
        // Create a temporary link and click it
        const link = document.createElement('a');
        link.href = data.videoUrl;
        link.download = 'basketball-analysis.mp4';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download video. Your access token may have expired.');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Reset errors
    setError(null);
    setVideoLoadError(null);
    
    // Check file extension as backup if MIME type fails
    const validExtensions = ['.mp4', '.mov', '.avi'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!file.type.startsWith('video/') && !hasValidExtension) {
      setError('Please upload a video file (MP4, MOV, or AVI)');
      return;
    }
    
    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      setError('File size must be less than 100MB');
      return;
    }
    
    setUploadedFile(file);
    await startProcessing(file);
  };

  const startProcessing = async (file) => {
    setCurrentStep('processing');
    
    // Mock mode for testing
    if (MOCK_MODE) {
      console.log('Running in MOCK MODE');
      setTimeout(() => {
        setAnalysisResults({
          total_shots: 10,
          shooting_percentage: 60,
          avg_form_score: 7.5
        });
        setVideoUrl(SAMPLE_VIDEO_URL);
        setPreviewUrl(SAMPLE_VIDEO_URL);
        setCurrentStep('results');
      }, 2000);
      return;
    }
    
    const formData = new FormData();
    formData.append('video', file);
    
    try {      
      const response = await fetch(`${API_URL}/analyze`, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `Upload failed with status: ${response.status}`);
      }
      
      const data = await response.json();      
      if (data.job_id) {
        setJobId(data.job_id);
      } else {
        throw new Error('No job ID received from server');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload video');
      setCurrentStep('upload');
    }
  };

  const resetApp = () => {
    setCurrentStep('upload');
    setUploadedFile(null);
    setJobId(null);
    setJobStatus(null);
    setAnalysisResults(null);
    setVideoUrl(null);
    setPreviewUrl(null);
    setError(null);
    setVideoLoadError(null);
    setPaymentCompleted(false);
    setPreviewPlaying(false);
    setAccessToken(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const testWithSampleVideo = () => {
    setAnalysisResults({
      total_shots: 10,
      shooting_percentage: 60,
      avg_form_score: 7.5
    });
    setVideoUrl(SAMPLE_VIDEO_URL);
    setPreviewUrl(SAMPLE_VIDEO_URL);
    setCurrentStep('results');
  };

  const UploadScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex flex-col">
      <div className="bg-white shadow-sm border-b border-orange-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI Basketball Coach</h1>
                <p className="text-gray-600">Analyze your shots with AI-powered coaching</p>
              </div>
            </div>
            {DEBUG_MODE && (
              <button
                onClick={testWithSampleVideo}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
              >
                Test with Sample Video
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Upload Your Practice Video</h2>
            <p className="text-gray-600 text-lg">Get instant AI analysis for just $9.99</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div
            className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200 ${
              dragActive
                ? 'border-orange-400 bg-orange-50'
                : 'border-gray-300 bg-white hover:border-orange-300 hover:bg-orange-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            <div className="space-y-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                <Upload className="w-8 h-8 text-orange-500" />
              </div>
              <div>
                <p className="text-xl font-medium text-gray-900 mb-2">
                  Drop your video here or click to browse
                </p>
                <p className="text-gray-500">MP4, MOV, AVI • Max 100MB</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3">📹 Recording tips:</h3>
            <ul className="space-y-2 text-gray-600">
              <li>Capture your full shooting motion</li>
              <li>Take 5-10 shots in one video</li>
              <li>Include the basketball hoop in frame</li>
              <li>Good lighting = better analysis</li>
            </ul>
          </div>

          {DEBUG_MODE && (
            <div className="mt-6 bg-gray-100 rounded-lg p-4 text-xs">
              <h4 className="font-bold mb-2">Debug Info:</h4>
              <div className="space-y-1 font-mono">
                <p>API URL: {API_URL}</p>
                <p>Mock Mode: {MOCK_MODE ? 'ON' : 'OFF'}</p>
              </div>
            </div>
          )}

          <div className="mt-6 text-center flex items-center justify-center space-x-4 text-gray-500">
            <div className="flex items-center">
              <Lock className="w-4 h-4 mr-1" />
              <span>Secure payment</span>
            </div>
            <span>•</span>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              <span>30-60 seconds analysis</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ProcessingScreen = () => {
    const getStageMessage = (stage) => {
      const messages = {
        'uploading': 'Uploading your video...',
        'downloading': 'Preparing video for analysis...',
        'analyzing': 'AI is analyzing your basketball shots...',
        'processing': 'Adding visual overlays to your video...',
        'completed': 'Analysis complete!'
      };
      return messages[stage] || 'Processing...';
    };

    const getProgressPercentage = () => {
      if (!jobStatus) return 0;
      const stages = {
        'queued': 10,
        'downloading': 25,
        'analyzing': 50,
        'processing': 75,
        'uploading': 90,
        'completed': 100
      };
      return stages[jobStatus.stage] || stages[jobStatus.status] || 0;
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="w-8 h-8 text-blue-500 animate-pulse" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Analyzing Your Shots</h2>
            <p className="text-gray-600 mb-8">
              {jobStatus?.stage ? getStageMessage(jobStatus.stage) : 'Processing your video...'}
            </p>
            
            <div className="space-y-4">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500">{getProgressPercentage()}% complete</p>
            </div>

            <div className="mt-8 space-y-3 text-left">
              <div className={`flex items-center space-x-3 ${getProgressPercentage() > 20 ? 'text-green-600' : 'text-gray-400'}`}>
                <CheckCircle className={`w-5 h-5 ${getProgressPercentage() > 20 ? 'text-green-500' : 'text-gray-300'}`} />
                <span className="text-sm">Detecting shot attempts</span>
              </div>
              <div className={`flex items-center space-x-3 ${getProgressPercentage() > 50 ? 'text-green-600' : 'text-gray-400'}`}>
                <CheckCircle className={`w-5 h-5 ${getProgressPercentage() > 50 ? 'text-green-500' : 'text-gray-300'}`} />
                <span className="text-sm">Analyzing shooting form</span>
              </div>
              <div className={`flex items-center space-x-3 ${getProgressPercentage() > 80 ? 'text-green-600' : 'text-gray-400'}`}>
                <CheckCircle className={`w-5 h-5 ${getProgressPercentage() > 80 ? 'text-green-500' : 'text-gray-300'}`} />
                <span className="text-sm">Generating coaching feedback</span>
              </div>
            </div>

            {DEBUG_MODE && (
              <div className="mt-6 bg-gray-100 rounded-lg p-3 text-xs text-left">
                <h4 className="font-bold mb-1">Debug Info:</h4>
                <div className="space-y-1 font-mono">
                  <p>Job ID: {jobId || 'Not set'}</p>
                  <p>Status: {jobStatus?.status || 'Unknown'}</p>
                  <p>Stage: {jobStatus?.stage || 'N/A'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ResultsScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100">
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Analysis Complete!</h1>
              <p className="text-gray-600">Your AI coaching report is ready</p>
            </div>
          </div>
          <button
            onClick={resetApp}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Analyze Another Video
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Error Alert */}
        {(error || videoLoadError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error || videoLoadError}</p>
            </div>
          </div>
        )}

        {/* Video Preview with Payment Gate */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          {!paymentCompleted ? (
            <div className="space-y-6">
              {/* Video Preview Section */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Preview Your Analysis</h3>
                
                {/* Limited Video Preview */}
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4">
                  {previewUrl ? (
                    <>
                      <video 
                        id="previewVideo"
                        className="w-full h-full"
                        src={previewUrl}
                        controls
                        controlsList="nodownload"
                        onPlay={() => {
                          setPreviewPlaying(true);
                        }}
                        onPause={() => setPreviewPlaying(false)}
                        onEnded={() => setPreviewPlaying(false)}
                        onError={(e) => {
                          console.error('Preview video error:', e);
                          const errorMessages = {
                            1: 'Video loading was aborted',
                            2: 'Network error while loading video',
                            3: 'Video decoding error',
                            4: 'Video format not supported'
                          };
                          
                          const errorCode = e.target.error?.code;
                          const errorMsg = errorMessages[errorCode] || 'Unknown video error';
                          
                          setVideoLoadError(`${errorMsg}`);
                        }}
                        onLoadedData={() => {
                          setVideoLoadError(null);
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                      
                      {/* Watermark Notice */}
                      <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white px-3 py-1 rounded text-sm">
                        15 Second Preview
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white">
                      <div className="text-center">
                        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
                        <p className="text-xl mb-2">Preview not available</p>
                        <p className="text-sm opacity-75">Waiting for video processing...</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 text-center mb-6">
                  👆 Watch a preview of your AI-analyzed video
                </p>
              </div>

              {/* Payment Section */}
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Unlock Full Video & Download
                </h3>
                <p className="text-gray-600 mb-6">
                  Get your complete coaching video with shot-by-shot feedback
                </p>
                
                <div className="max-w-md mx-auto">
                  <Elements stripe={stripePromise}>
                    <SecurePaymentForm 
                      jobId={jobId}
                      onSuccess={handlePaymentSuccess} 
                    />
                  </Elements>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900">Your Full Coaching Video</h3>
              
              {/* Full Video Player */}
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                {videoUrl ? (
                  <video 
                    controls 
                    className="w-full h-full"
                    src={videoUrl}
                    controlsList="nodownload"
                    onError={(e) => {
                      console.error('Full video error:', e);
                      setVideoLoadError('Unable to load full video. Please try downloading.');
                    }}
                    onLoadedData={() => {
                      setVideoLoadError(null);
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <div className="text-center">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
                      <p className="text-xl">Loading video...</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Secure Download Button */}
              {videoUrl && accessToken && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={verifyAndDownload}
                    className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download Video</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Debug Info Panel */}
        {DEBUG_MODE && (
          <div className="bg-gray-100 rounded-lg p-4 text-sm">
            <h4 className="font-bold mb-2">Debug Info:</h4>
            <div className="space-y-1 font-mono text-xs">
              <p>Video URL: {videoUrl || 'Not set'}</p>
              <p>Preview URL: {previewUrl || 'Not set'}</p>
              <p>Job ID: {jobId || 'Not set'}</p>
              <p>API URL: {API_URL}</p>
              <p>Analysis Results: {analysisResults ? 'Loaded' : 'Not loaded'}</p>
              <p>Payment Status: {paymentCompleted ? 'Completed' : 'Pending'}</p>
              <p>Access Token: {accessToken ? 'Set' : 'Not set'}</p>
              <p>Video Error: {videoLoadError || 'None'}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {paymentCompleted && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              Payment Successful!
            </h3>
            <p className="text-green-700">
              Thank you for your purchase. Your coaching video is now available for download.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="font-sans">
      {currentStep === 'upload' && <UploadScreen />}
      {currentStep === 'processing' && <ProcessingScreen />}
      {currentStep === 'results' && <ResultsScreen />}
    </div>
  );
};

// Main App Component
function App() {
  return (
    <Elements stripe={stripePromise}>
      <BasketballCoachApp />
    </Elements>
  );
}

export default App;